import React from "react";
import { FiCalendar, FiMapPin, FiAnchor, FiCheckCircle } from "react-icons/fi";

const formatDate = (value) => {
  if (!value) return "Pending";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Pending";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const addDays = (dateValue, days) => {
  if (!dateValue) return null;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return d;
};

const getStepIndex = (status) => {
  const s = String(status || "").toLowerCase();

  if (s === "delivered") return 5;
  if (s === "customs cleared") return 4;
  if (s === "arrived at port") return 3;
  if (s === "in transit") return 2;
  if (s === "departed from origin port") return 1;
  return 0;
};

export default function ContainerJourneyTimeline({ shipment }) {
  if (!shipment) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
        No shipment data available.
      </div>
    );
  }

  const baseDate = shipment.date ? new Date(shipment.date) : new Date();
  const stepIndex = getStepIndex(shipment.status);

  const steps = [
    {
      title: `Booking / BL No: ${shipment.bookingBLNo || "-"}`,
      date: shipment.date,
      icon: FiCalendar,
    },
    {
      title: `Departed from ${shipment.origin || "Origin Port"}`,
      date: addDays(baseDate, 3),
      icon: FiAnchor,
    },
    {
      title: "In Transit",
      date: addDays(baseDate, 11),
      icon: FiMapPin,
    },
    {
      title: `Arrived at ${shipment.destination || "UK Port"}`,
      date: addDays(baseDate, 19),
      icon: FiMapPin,
    },
    {
      title: "Customs Cleared",
      date: addDays(baseDate, 22),
      icon: FiCheckCircle,
    },
    {
      title: "Delivery Booked On",
      date: shipment.deliveryBookedOn || shipment.expectedArrival || addDays(baseDate, 28),
      icon: FiCalendar,
    },
  ];

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Live Shipment Tracking
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
            {shipment.containerNumber || shipment.bookingBLNo || "-"}
          </h3>
          <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300">
            {shipment.routeState || shipment.status || "In Transit"}
          </span>
        </div>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {shipment.origin || "China"} → {shipment.destination || "United Kingdom"}
        </p>
      </div>

      <div className="space-y-4">
        {steps.map((step, index) => {
          const active = index === stepIndex;
          const done = index < stepIndex;
          const Icon = step.icon;

          return (
            <div key={step.title} className="relative pl-10">
              {index !== steps.length - 1 && (
                <span className="absolute left-[15px] top-8 h-[calc(100%-8px)] w-px bg-slate-200 dark:bg-slate-800" />
              )}

              <span
                className={`absolute left-0 top-0 grid h-8 w-8 place-items-center rounded-full border-2 ${
                  done
                    ? "border-emerald-500 bg-emerald-500 text-white"
                    : active
                    ? "border-blue-500 bg-blue-500 text-white"
                    : "border-slate-300 bg-white text-slate-400 dark:border-slate-700 dark:bg-slate-900"
                }`}
              >
                <Icon size={14} />
              </span>

              <div className="pb-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {step.title}
                </p>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {formatDate(step.date)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}